import React, { useState } from 'react';
import './WhatsAppCampaign.scss';

const WhatsAppCampaign = ({ onClose, onSave, editingCampaign = null }) => {
  const [campaignData, setCampaignData] = useState({
    name: editingCampaign?.name || '',
    message: editingCampaign?.message || '',
    fromNumber: editingCampaign?.fromNumber || '',
    segment: editingCampaign?.segment || 'all_candidates',
    scheduledAt: editingCampaign?.scheduledAt || '',
    isScheduled: editingCampaign?.isScheduled || false,
    template: editingCampaign?.template || 'custom',
    mediaType: editingCampaign?.mediaType || 'text',
    mediaUrl: editingCampaign?.mediaUrl || '',
    buttons: editingCampaign?.buttons || []
  });

  const [activeTab, setActiveTab] = useState('content');
  const [charCount, setCharCount] = useState(campaignData.message.length);

  // WhatsApp templates
  const whatsappTemplates = {
    interview_reminder: {
      name: 'Interview Reminder',
      message: `Hi {{first_name}}! 👋\n\nThis is a friendly reminder about your interview with {{company_name}} tomorrow.\n\n📅 Date: {{interview_date}}\n⏰ Time: {{interview_time}}\n📍 Location: {{interview_location}}\n\nPlease reply YES to confirm your attendance. Good luck! 🍀`
    },
    application_received: {
      name: 'Application Received',
      message: `Hi {{first_name}}! ✨\n\nThank you for applying to the {{position_name}} role at {{company_name}}.\n\nWe've received your application and our team will review it within 3-5 business days.\n\nWe'll keep you updated on the next steps! 🚀`
    },
    interview_invitation: {
      name: 'Interview Invitation',
      message: `Hi {{first_name}}! 🎉\n\nWe're impressed with your profile and would love to invite you for an interview for the {{position_name}} position at {{company_name}}.\n\nAre you available on {{interview_date}}?\n\nPlease reply with your preferred time slot. Looking forward to meeting you! 😊`
    },
    offer_extended: {
      name: 'Job Offer',
      message: `Congratulations {{first_name}}! 🎊\n\nWe're thrilled to extend an offer for the {{position_name}} position at {{company_name}}.\n\nPlease check your email for the complete offer details.\n\nWelcome to the team! 🤝`
    },
    status_update: {
      name: 'Application Status',
      message: `Hi {{first_name}}! 📋\n\nUpdate on your {{position_name}} application:\n\n{{status_message}}\n\nFor more details, please check your email or reply to this message.\n\nThank you for your patience! 🙏`
    },
    welcome_message: {
      name: 'Welcome Message',
      message: `Welcome to {{company_name}}, {{first_name}}! 🌟\n\nWe're excited to have you join our talent community.\n\n✅ Your profile has been registered\n📝 Keep an eye out for relevant opportunities\n💼 Update your preferences anytime\n\nLet's build something amazing together! 🚀`
    }
  };

  // Candidate segments
  const segmentOptions = [
    { value: 'all_candidates', label: 'All Candidates', count: 1234 },
    { value: 'new_applicants', label: 'New Applicants (Last 7 days)', count: 89 },
    { value: 'interview_scheduled', label: 'Interview Scheduled', count: 45 },
    { value: 'interview_today', label: 'Interview Today', count: 8 },
    { value: 'interview_tomorrow', label: 'Interview Tomorrow', count: 12 },
    { value: 'pending_response', label: 'Pending Response', count: 156 },
    { value: 'offer_pending', label: 'Offer Pending', count: 23 },
    { value: 'active_prospects', label: 'Active Prospects', count: 234 }
  ];

  // Available merge tags
  const mergeTags = [
    { tag: '{{first_name}}', description: 'Candidate\'s first name' },
    { tag: '{{last_name}}', description: 'Candidate\'s last name' },
    { tag: '{{full_name}}', description: 'Candidate\'s full name' },
    { tag: '{{position_name}}', description: 'Applied position title' },
    { tag: '{{company_name}}', description: 'Your company name' },
    { tag: '{{interview_date}}', description: 'Interview date' },
    { tag: '{{interview_time}}', description: 'Interview time' },
    { tag: '{{interview_location}}', description: 'Interview location' },
    { tag: '{{application_id}}', description: 'Application reference' },
    { tag: '{{status_message}}', description: 'Current status message' }
  ];

  // Media types
  const mediaTypes = [
    { value: 'text', label: 'Text Only', icon: '💬' },
    { value: 'image', label: 'Image', icon: '🖼️' },
    { value: 'document', label: 'Document', icon: '📄' },
    { value: 'video', label: 'Video', icon: '🎥' }
  ];

  const handleSave = () => {
    if (!campaignData.name.trim()) {
      alert('Please enter a campaign name');
      return;
    }
    if (!campaignData.message.trim()) {
      alert('Please enter WhatsApp message');
      return;
    }
    if (campaignData.message.length > 4096) {
      alert('WhatsApp message must be 4096 characters or less');
      return;
    }

    onSave({
      ...campaignData,
      type: 'whatsapp',
      createdAt: new Date().toISOString()
    });
  };

  const handleTemplateSelect = (templateKey) => {
    const template = whatsappTemplates[templateKey];
    setCampaignData(prev => ({
      ...prev,
      template: templateKey,
      message: template.message
    }));
    setCharCount(template.message.length);
  };

  const handleMessageChange = (value) => {
    setCampaignData(prev => ({
      ...prev,
      message: value
    }));
    setCharCount(value.length);
  };

  const insertMergeTag = (tag) => {
    const newContent = campaignData.message + ' ' + tag + ' ';
    setCampaignData(prev => ({
      ...prev,
      message: newContent
    }));
    setCharCount(newContent.length);
  };

  const getEstimatedReach = () => {
    const segment = segmentOptions.find(s => s.value === campaignData.segment);
    return segment ? segment.count : segmentOptions[0]?.count || 0;
  };

  const getMessageParts = () => {
    return Math.ceil(campaignData.message.length / 1600); // WhatsApp message length
  };

  const addButton = () => {
    if (campaignData.buttons.length < 3) {
      setCampaignData(prev => ({
        ...prev,
        buttons: [...prev.buttons, { text: '', url: '', type: 'url' }]
      }));
    }
  };

  const removeButton = (index) => {
    setCampaignData(prev => ({
      ...prev,
      buttons: prev.buttons.filter((_, i) => i !== index)
    }));
  };

  const updateButton = (index, field, value) => {
    setCampaignData(prev => ({
      ...prev,
      buttons: prev.buttons.map((btn, i) => 
        i === index ? { ...btn, [field]: value } : btn
      )
    }));
  };

  return (
    <div className="whatsapp-campaign-modal">
      <div className="whatsapp-campaign-container">
        {/* Header */}
        <div className="campaign-header">
          <div className="header-content">
            <h2>💚 {editingCampaign ? 'Edit WhatsApp Campaign' : 'Create WhatsApp Campaign'}</h2>
            <p>Send targeted WhatsApp messages to your candidates</p>
          </div>
          <div className="header-actions">
            <button className="btn btn-secondary" onClick={onClose}>
              Cancel
            </button>
            <button className="btn btn-primary" onClick={handleSave}>
              {editingCampaign ? 'Update Campaign' : 'Save Campaign'}
            </button>
          </div>
        </div>

        <div className="campaign-content">
          {/* Navigation Tabs */}
          <div className="campaign-tabs">
            <button
              className={`tab-btn ${activeTab === 'content' ? 'active' : ''}`}
              onClick={() => setActiveTab('content')}
            >
              💬 Content
            </button>
            <button
              className={`tab-btn ${activeTab === 'audience' ? 'active' : ''}`}
              onClick={() => setActiveTab('audience')}
            >
              🎯 Audience
            </button>
            <button
              className={`tab-btn ${activeTab === 'schedule' ? 'active' : ''}`}
              onClick={() => setActiveTab('schedule')}
            >
              📅 Schedule
            </button>
          </div>

          {/* Tab Content */}
          {activeTab === 'content' && (
            <div className="tab-content">
              <div className="content-section">
                {/* Campaign Settings */}
                <div className="form-section">
                  <h3>Campaign Details</h3>
                  
                  <div className="form-group">
                    <label>Campaign Name *</label>
                    <input
                      type="text"
                      value={campaignData.name}
                      onChange={(e) => setCampaignData(prev => ({
                        ...prev,
                        name: e.target.value
                      }))}
                      placeholder="e.g., Interview Reminder WhatsApp"
                      className="form-input"
                    />
                  </div>

                  <div className="form-group">
                    <label>From Number</label>
                    <input
                      type="text"
                      value={campaignData.fromNumber}
                      onChange={(e) => setCampaignData(prev => ({
                        ...prev,
                        fromNumber: e.target.value
                      }))}
                      placeholder="e.g., Your Business WhatsApp Number"
                      className="form-input"
                    />
                  </div>
                </div>

                {/* Media Type Selection */}
                <div className="media-section">
                  <h3>Message Type</h3>
                  <div className="media-types">
                    {mediaTypes.map(type => (
                      <div
                        key={type.value}
                        className={`media-type ${campaignData.mediaType === type.value ? 'active' : ''}`}
                        onClick={() => setCampaignData(prev => ({ ...prev, mediaType: type.value }))}
                      >
                        <div className="media-icon">{type.icon}</div>
                        <div className="media-label">{type.label}</div>
                      </div>
                    ))}
                  </div>

                  {campaignData.mediaType !== 'text' && (
                    <div className="form-group">
                      <label>Media URL</label>
                      <input
                        type="url"
                        value={campaignData.mediaUrl}
                        onChange={(e) => setCampaignData(prev => ({
                          ...prev,
                          mediaUrl: e.target.value
                        }))}
                        placeholder="https://example.com/media.jpg"
                        className="form-input"
                      />
                    </div>
                  )}
                </div>

                {/* WhatsApp Templates */}
                <div className="templates-section">
                  <h3>WhatsApp Templates</h3>
                  <div className="templates-grid">
                    <div
                      className={`template-card ${campaignData.template === 'custom' ? 'active' : ''}`}
                      onClick={() => setCampaignData(prev => ({ ...prev, template: 'custom' }))}
                    >
                      <div className="template-icon">✏️</div>
                      <div className="template-name">Custom Message</div>
                      <div className="template-desc">Start from scratch</div>
                    </div>
                    
                    {Object.entries(whatsappTemplates).map(([key, template]) => (
                      <div
                        key={key}
                        className={`template-card ${campaignData.template === key ? 'active' : ''}`}
                        onClick={() => handleTemplateSelect(key)}
                      >
                        <div className="template-icon">💚</div>
                        <div className="template-name">{template.name}</div>
                        <div className="template-desc">Pre-built template</div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Message Editor */}
                <div className="message-editor-section">
                  <div className="message-header">
                    <h3>WhatsApp Message</h3>
                    <div className="message-stats">
                      <div className="char-count">
                        <span className={charCount > 4096 ? 'over-limit' : ''}>{charCount}</span>/4096 characters
                      </div>
                      <div className="message-parts">
                        {getMessageParts()} part{getMessageParts() > 1 ? 's' : ''}
                      </div>
                    </div>
                    <div className="merge-tags-dropdown">
                      <button className="merge-tags-btn">
                        + Insert Merge Tag
                      </button>
                      <div className="merge-tags-list">
                        {mergeTags.map(tag => (
                          <div
                            key={tag.tag}
                            className="merge-tag-item"
                            onClick={() => insertMergeTag(tag.tag)}
                          >
                            <code>{tag.tag}</code>
                            <span>{tag.description}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                  
                  <div className="form-group">
                    <textarea
                      value={campaignData.message}
                      onChange={(e) => handleMessageChange(e.target.value)}
                      placeholder="Enter your WhatsApp message here... You can use emojis! 😊"
                      className="form-textarea whatsapp-message"
                      rows="8"
                    />
                  </div>
                </div>

                {/* Interactive Buttons */}
                <div className="buttons-section">
                  <div className="buttons-header">
                    <h3>Interactive Buttons</h3>
                    <button className="btn btn-secondary" onClick={addButton}>
                      + Add Button
                    </button>
                  </div>
                  
                  {campaignData.buttons.map((button, index) => (
                    <div key={index} className="button-editor">
                      <div className="button-inputs">
                        <input
                          type="text"
                          value={button.text}
                          onChange={(e) => updateButton(index, 'text', e.target.value)}
                          placeholder="Button text"
                          className="form-input"
                        />
                        <input
                          type="url"
                          value={button.url}
                          onChange={(e) => updateButton(index, 'url', e.target.value)}
                          placeholder="Button URL (optional)"
                          className="form-input"
                        />
                      </div>
                      <button 
                        className="btn-remove" 
                        onClick={() => removeButton(index)}
                      >
                        ✕
                      </button>
                    </div>
                  ))}
                  
                  {campaignData.buttons.length === 0 && (
                    <p className="no-buttons">No interactive buttons added. Add up to 3 buttons to make your message more engaging.</p>
                  )}
                </div>
              </div>
            </div>
          )}

          {activeTab === 'audience' && (
            <div className="tab-content">
              <div className="audience-section">
                <h3>Select Audience</h3>
                
                <div className="segments-list">
                  {segmentOptions.map(segment => (
                    <div
                      key={segment.value}
                      className={`segment-item ${campaignData.segment === segment.value ? 'selected' : ''}`}
                      onClick={() => setCampaignData(prev => ({
                        ...prev,
                        segment: segment.value
                      }))}
                    >
                      <div className="segment-info">
                        <div className="segment-name">{segment.label}</div>
                        <div className="segment-description">
                          {segment.count} candidates will receive this WhatsApp message
                        </div>
                      </div>
                      <div className="segment-count">
                        {segment.count.toLocaleString()}
                      </div>
                    </div>
                  ))}
                </div>

                <div className="audience-summary">
                  <div className="summary-card">
                    <h4>📊 Audience Summary</h4>
                    <div className="summary-stats">
                      <div className="stat">
                        <div className="stat-label">Selected Segment</div>
                        <div className="stat-value">
                          {segmentOptions.find(s => s.value === campaignData.segment)?.label}
                        </div>
                      </div>
                      <div className="stat">
                        <div className="stat-label">Total Recipients</div>
                        <div className="stat-value">{getEstimatedReach().toLocaleString()}</div>
                      </div>
                      <div className="stat">
                        <div className="stat-label">Estimated Reach</div>
                        <div className="stat-value">
                          {((getEstimatedReach() / 1234) * 100).toFixed(1)}%
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'schedule' && (
            <div className="tab-content">
              <div className="schedule-section">
                <h3>Send Options</h3>
                
                <div className="send-options">
                  <div
                    className={`send-option ${!campaignData.isScheduled ? 'selected' : ''}`}
                    onClick={() => setCampaignData(prev => ({ ...prev, isScheduled: false }))}
                  >
                    <div className="option-radio">
                      <div className={`radio ${!campaignData.isScheduled ? 'checked' : ''}`}></div>
                    </div>
                    <div className="option-info">
                      <div className="option-title">Send Immediately</div>
                      <div className="option-desc">WhatsApp message will be sent right after saving</div>
                    </div>
                  </div>
                  
                  <div
                    className={`send-option ${campaignData.isScheduled ? 'selected' : ''}`}
                    onClick={() => setCampaignData(prev => ({ ...prev, isScheduled: true }))}
                  >
                    <div className="option-radio">
                      <div className={`radio ${campaignData.isScheduled ? 'checked' : ''}`}></div>
                    </div>
                    <div className="option-info">
                      <div className="option-title">Schedule for Later</div>
                      <div className="option-desc">Choose a specific date and time</div>
                    </div>
                  </div>
                </div>

                {campaignData.isScheduled && (
                  <div className="schedule-datetime">
                    <div className="form-group">
                      <label>Scheduled Date & Time</label>
                      <input
                        type="datetime-local"
                        value={campaignData.scheduledAt}
                        onChange={(e) => setCampaignData(prev => ({
                          ...prev,
                          scheduledAt: e.target.value
                        }))}
                        min={new Date().toISOString().slice(0, 16)}
                        className="form-input"
                      />
                    </div>
                  </div>
                )}

                <div className="schedule-summary">
                  <div className="summary-card">
                    <div className="summary-icon">📱</div>
                    <div className="summary-info">
                      <div className="summary-title">Delivery Time</div>
                      <div className="summary-value">
                        {campaignData.isScheduled && campaignData.scheduledAt
                          ? new Date(campaignData.scheduledAt).toLocaleString()
                          : 'Immediately after save'
                        }
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default WhatsAppCampaign;